import { useToken } from "./tokenContext";
import axios from "axios";
import https from "https-browserify";

const useApi = () => {
  const { token } = useToken();
  // Full API base URL (scheme + host + optional port). E.g.:
  // http://192.168.15.152:8000 or https://api.lubtrack.com (no port).
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  // Encerra a sessão e força novo login quando o JWT expira (spec 015: a API
  // responde 401 para token expirado; 400 para ausente/inválido). Evita loop de
  // redirecionamento e sinaliza o motivo para a tela de login.
  const handleTokenExpired = () => {
    try {
      localStorage.removeItem("user");
      sessionStorage.setItem("sessionExpired", "1");
    } catch {
      /* storage indisponível — segue para o redirect */
    }
    if (!window.location.pathname.startsWith("/login")) {
      window.location.assign("/login");
    }
  };

  // Helper to create API instances with the configured base URL
  const createApiInstance = () => {
    const instance = axios.create({
      baseURL: apiBaseUrl,
      httpsAgent: new https.Agent({ rejectUnauthorized: false }),
    });

    // Só intercepta expiração em chamadas autenticadas (com token). Em /login e
    // /forgot-password (sem token) um 401 é erro de credencial, não expiração.
    if (token) {
      instance.interceptors.response.use(
        (response) => response,
        (error) => {
          if (error?.response?.status === 401) {
            handleTokenExpired();
          }
          return Promise.reject(error);
        },
      );
    }

    return instance;
  };

  const fetchData = async (url, params) => {
    if (!token) {
      throw new Error("Token de autenticação não encontrado.");
    }
    const api = createApiInstance();
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    const response = await api.get(url, {
      headers,
      params,
    });
    if (
      response.data &&
      typeof response.data === "object" &&
      "data" in response.data
    ) {
      const mainData = response.data.data;
      Object.keys(response.data).forEach((key) => {
        if (key !== "data") {
          response[key] = response.data[key];
        }
      });
      response.data = mainData;
    }
    return response;
  };

  const deleteData = async (url) => {
    if (!token) {
      throw new Error("Token de autenticação não encontrado.");
    }
    const api = createApiInstance();
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    const response = await api.delete(url, {
      headers,
    });
    response.data = response.data.data;
    return response;
  };

  const updateData = async (url, data) => {
    if (!token) {
      throw new Error("Token de autenticação não encontrado.");
    }
    const api = createApiInstance();
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    const response = await api.patch(url, data, {
      headers,
    });
    response.data = response.data.data;
    return response;
  };

  const postData = async (url, data) => {
    if (!token) {
      throw new Error("Token de autenticação não encontrado.");
    }
    const api = createApiInstance();
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    const response = await api.post(url, data, {
      headers,
    });
    if (
      response.data &&
      typeof response.data === "object" &&
      "data" in response.data
    ) {
      const mainData = response.data.data;
      Object.keys(response.data).forEach((key) => {
        if (key !== "data") {
          response[key] = response.data[key];
        }
      });
      response.data = mainData;
    }
    return response;
  };

  // Exporta CSV a partir de rotas que negociam o formato pelo header Accept
  // (ex.: POST /event-timeline/history com Accept: text/csv). Retorna o texto
  // bruto do CSV; o download em si é montado pelo chamador (Blob + anchor).
  const postCsv = async (url, data) => {
    if (!token) {
      throw new Error("Token de autenticação não encontrado.");
    }
    const api = createApiInstance();
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      Accept: "text/csv",
    };
    const response = await api.post(url, data, {
      headers,
      responseType: "text",
    });
    return response.data;
  };

  const validatePhonePost = async (url, data, authToken) => {
    if (!authToken) {
      throw new Error("Token de autenticação não encontrado.");
    }

    const api = createApiInstance();
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    };
    const response = await api.post(url, data, {
      headers,
    });
    if (
      response.data &&
      typeof response.data === "object" &&
      "data" in response.data
    ) {
      const mainData = response.data.data;
      Object.keys(response.data).forEach((key) => {
        if (key !== "data") {
          response[key] = response.data[key];
        }
      });
      response.data = mainData;
    }
    return response;
  };

  const forgotPassword = async (url, data) => {
    const api = createApiInstance();
    const response = await api.post(url, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    response.data = response.data.data;
    return response;
  };

  const userLogin = async (url, data) => {
    const api = createApiInstance();
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    // The first request to a cold/idle stack (a scaled-to-zero container or a
    // pooled DB connection reaped by an upstream proxy/LB above the app) can
    // surface as a network error or 5xx and then succeed on an immediate
    // retry — the origin of the "first click fails, second click works" login.
    // A single automatic retry makes the first click behave like that manual
    // second click. A 4xx (bad credential) is never retried.
    const isRetriable = (error) => {
      const status = error?.response?.status;
      return !status || status >= 500;
    };

    try {
      return await api.post(url, data, config);
    } catch (error) {
      if (!isRetriable(error)) {
        throw error;
      }
      return await api.post(url, data, config);
    }
  };

  return {
    fetchData,
    deleteData,
    updateData,
    postData,
    postCsv,
    forgotPassword,
    userLogin,
    validatePhonePost,
  };
};

export default useApi;
